import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  FileText,
  Briefcase,
  MessageSquare,
  FolderOpen,
  Settings,
  Users,
  ShieldCheck,
  Heart,
  UserCheck,
  Upload,
  Monitor,
  BarChart3,
} from "lucide-react";
import logoLight from "@/assets/logo-light.png";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { db } from "@/lib/firebase";
import { doc, getDoc } from "firebase/firestore";

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ElementType;
  roles?: string[]; // If specified, only show for these roles
}

const navigation: NavigationItem[] = [
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { name: "Submissions", href: "/submissions", icon: FileText },
  { name: "Submit Deal", href: "/submit-deal", icon: Upload },
  { name: "Deals", href: "/deals", icon: Briefcase },
  { name: "Listings", href: "/listings", icon: Monitor },
  { name: "Favorites", href: "/favorites", icon: Heart },
  { name: "Messages", href: "/messages", icon: MessageSquare },
  { name: "Documents", href: "/documents", icon: FolderOpen },
  { name: "Partners", href: "/partners", icon: Users },
  { name: "Consultants", href: "/consultants", icon: UserCheck },
  { name: "Admin Portal", href: "/admin/overview", icon: Settings, roles: ["admin"] },
  { name: "CIM Review", href: "/cim-review", icon: FileText, roles: ["admin", "reviewer"] },
  { name: "Set Roles", href: "/admin-set-role", icon: ShieldCheck, roles: ["admin"] },
  { name: "KPI Dashboard", href: "/kpi-dashboard", icon: BarChart3, roles: ["admin"] },
];

export const DashboardSidebar = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user) return;

      try {
        const userDoc = await getDoc(doc(db, "users", user.id));
        if (userDoc.exists()) {
          setUserRole(userDoc.data()?.role || "user");
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
      }
    };

    fetchUserRole();
  }, [user]);

  const filteredNavigation = navigation.filter((item) => {
    if (!item.roles) return true;
    return userRole && item.roles.includes(userRole);
  });

  return (
    <aside className="w-64 bg-card border-r border-border flex flex-col">
      <div className="p-6 border-b border-border">
        <img src={logoLight} alt="AcquizGroup" className="h-16 w-auto" />
        <p className="text-sm text-muted-foreground mt-2">Deal Platform</p>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {filteredNavigation.map((item) => {
          const isActive = location.pathname === item.href;
          const Icon = item.icon;

          return (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground",
              )}
            >
              <Icon className="h-5 w-5" />
              {item.name}
            </Link>
          );
        })}
      </nav>

      {user && (
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user.email}</p>
              <p className="text-xs text-muted-foreground capitalize">{userRole || "User"}</p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};
